import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import roles from "helpers/roles";
import { FULL_ACCESS } from "constants/AccessTypes/AccessTypes";
import NavDetails from "components/Elements/NavDetails";
import TabTitle from "components/Elements/TabTitle";
import PrintIcon from "assets/printer.svg";
import CommonDropdown from "components/Elements/CommonDropdown";
import {
	ScheduleReportModal,
	buildScheduleReportFooterItem,
	useScheduleReportModal,
	ReportGenerationDialog,
} from "components/Modules/ReportSubscriptions";
import { useCreateReportSubscription } from "components/Modules/ReportSubscriptions/hooks/useCreateReportSubscription";
import {
	formValuesToApiRequest,
	toApiReportType,
} from "services/reportSubscriptions";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";
import { appPath, newAnalyticsPath } from "helpers/routePaths";
import { getLocalStorageData, defaultPageSize } from "helpers/utils";
import { makeGetNextPageParam, flattenPages } from "helpers/infiniteQuery";
import { getAuditHistoryList, getAuditHistoryCount, getAuditUsers } from "services/audits";
import AuditHistoryTable from "./AuditHistoryTable";
import { buildAuditColumns } from "./auditColumns";

export const isSiteAdministrator = ({ role, position }) =>
	role === roles.superAdmin || position?.settingsAccess === FULL_ACCESS;

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()(() => ({
	wrapper: {
		display: "flex",
		alignItems: "flex-start",
		[media]: {
			marginTop: "10px",
			justifyContent: "space-between",
			flexDirection: "column",
		},
		gap: "2rem",
	},
}));

// No defaultRange: the default is Show All like every other list, so the shared
// TimeframeFilter's Show All handling (URL param cleared) round-trips correctly
const filterConfig = {
	filters: {
		timeframe: {
			allowCustomRange: true,
			allowedRanges: [
				"today", "yesterday", "this-week", "last-week", "last-month", "last-year",
				"last-7-days", "last-14-days", "last-30-days", "last-90-days", "custom",
			],
		},
		user: { fetchFn: getAuditUsers },
		entityType: true,
		actionType: true,
	},
};

const buildAuditReports = (t) => [
	{ id: "1", name: t("title.auditHistory"), reportType: "auditHistory" },
	{ id: "2", name: t("title.auditHistoryExcel"), reportType: "auditHistoryCsv" },
];

function Header({ countLabel }) {
	const { classes } = useStyles();
	const { t } = useTranslation();
	const { buildReportFilterContext } = useFilters();
	const auditReports = useMemo(() => buildAuditReports(t), [t]);
	const [activeReportRequest, setActiveReportRequest] = useState(null);

	const scheduleModal = useScheduleReportModal();
	const createSubscription = useCreateReportSubscription();
	const handleScheduleSubmit = async (payload) => {
		const apiBody = formValuesToApiRequest(payload, payload.filterValues, payload.filterSummary);
		await createSubscription(apiBody);
	};

	const handleReportClick = (report, handleClose) => {
		if (!report || Object.keys(report).length < 1) return;
		handleClose();
		setActiveReportRequest({
			reportName: report.name,
			body: {
				reportType: toApiReportType(report.reportType),
				filterContext: buildReportFilterContext(),
				name: null,
				recipientEmail: null,
				sendEmail: false,
			},
		});
	};

	return (
		<div className={"topContainerCustomCaptions"}>
			<NavDetails
				status={false}
				lastSaved={""}
				staticCrumbs={[
					{ id: 1, name: t("label.analytics"), url: appPath + newAnalyticsPath },
					{ id: 2, name: `${t("title.auditHistory")} (${countLabel})`, url: "" },
				]}
				hideLastLogin
				hideLastSave
				hideVersion={true}
			/>
			{activeReportRequest ? (
				<ReportGenerationDialog
					open
					onClose={() => setActiveReportRequest(null)}
					reportName={activeReportRequest.reportName}
					body={activeReportRequest.body}
				/>
			) : null}
			<div className={classes.wrapper}>
				<CommonDropdown
					iconSrc={PrintIcon}
					isDownloading={false}
					dataSource={auditReports}
					dynamicAction={handleReportClick}
					footerItem={buildScheduleReportFooterItem(scheduleModal.openModal)}
				/>
			</div>
			{scheduleModal.open ? (
				<ScheduleReportModal
					open
					onClose={scheduleModal.closeModal}
					availableReports={auditReports}
					initialValues={scheduleModal.initialValues}
					onSubmit={handleScheduleSubmit}
				/>
			) : null}
		</div>
	);
}

function AuditHistoryContent() {
	const { t } = useTranslation();
	const { filterValues, allFiltersInitialized } = useFilters();
	const { siteAppID } = getLocalStorageData("me");
	const [pageSize] = useState(() => defaultPageSize());
	const [sortOrder, setSortOrder] = useState("desc");

	const columns = useMemo(() => buildAuditColumns(t), [t]);

	const timeframeValue = filterValues.timeframe?.value;
	const currentFilters = useMemo(() => ({
		fromDate: timeframeValue?.fromDate || "",
		toDate: timeframeValue?.toDate || "",
		entityTypes: filterValues.entityType?.params?.entityTypes || [],
		actions: filterValues.actionType?.params?.actions || [],
		userIds: filterValues.user?.params?.userId ? [filterValues.user.params.userId] : [],
	}), [
		timeframeValue?.fromDate,
		timeframeValue?.toDate,
		filterValues.entityType?.params?.entityTypes,
		filterValues.actionType?.params?.actions,
		filterValues.user?.params?.userId,
	]);

	const auditsQuery = useInfiniteQuery({
		queryKey: ["audits", "list", { siteAppID, ...currentFilters, sortOrder, pageSize }],
		enabled: allFiltersInitialized && Boolean(siteAppID),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const response = await getAuditHistoryList({
				...currentFilters,
				pageNumber: pageParam,
				pageSize,
				sortOrder,
			});
			return { rows: Array.isArray(response.data) ? response.data : [], pageParam };
		},
		getNextPageParam: makeGetNextPageParam(pageSize),
	});

	const countQuery = useQuery({
		queryKey: ["audits", "count", { siteAppID, ...currentFilters }],
		enabled: allFiltersInitialized && Boolean(siteAppID),
		queryFn: async () => {
			const response = await getAuditHistoryCount(currentFilters);
			return response?.data ?? { count: 0, capped: false };
		},
	});

	const rows = useMemo(() => flattenPages(auditsQuery.data) || [], [auditsQuery.data]);

	const countLabel = countQuery.data?.capped
		? `${countQuery.data.count.toLocaleString()}+`
		: `${(countQuery.data?.count ?? 0).toLocaleString()}`;

	return (
		<>
			<Header countLabel={countLabel} />
			<FilterContainer />
			<Typography style={{ margin: "8px 0", fontSize: 14, color: "rgba(0,0,0,0.6)" }}>
				{countQuery.isFetching ? t("message.counting") : t("label.recordsCount", { count: countLabel })}
			</Typography>
			<AuditHistoryTable
				columns={columns}
				rows={rows}
				sortOrder={sortOrder}
				onToggleSort={() => setSortOrder((order) => (order === "desc" ? "asc" : "desc"))}
				hasNextPage={auditsQuery.hasNextPage}
				isFetchingNextPage={auditsQuery.isFetchingNextPage}
				fetchNextPage={auditsQuery.fetchNextPage}
				isFetchNextPageError={auditsQuery.isFetchNextPageError}
			/>
		</>
	);
}

const AuditHistoryReport = () => {
	const { t } = useTranslation();

	// Site administration only (internal v1 decision); the API enforces the same
	if (!isSiteAdministrator(getLocalStorageData("me"))) {
		return (
			<>
				<TabTitle title={t("title.auditHistory")} />
				<Typography style={{ marginTop: 24 }}>
					{t("message.auditHistorySiteAdminOnly")}
				</Typography>
			</>
		);
	}

	return (
		<>
			<TabTitle title={t("title.auditHistory")} />
			<FilterProvider config={filterConfig} storageKey="audit-history">
				<AuditHistoryContent />
			</FilterProvider>
		</>
	);
};

export default AuditHistoryReport;
